import {CommentModel, commentStatus} from '../../../../core/models/comment-model';
import {patchState, signalStore, withComputed, withMethods, withState} from '@ngrx/signals';
import {computed, inject} from '@angular/core';
import {CheckService} from '../../../../core/checks/check';
import {DocumentService} from '../../../../core/documents/document';
import {CommentService} from '../../../../core/comments/comment';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {switchMap, tap} from 'rxjs/operators';
import {firstValueFrom, pipe} from 'rxjs';
import { tapResponse } from '@ngrx/operators';

type CommentState = {
  comments: CommentModel[];
  isLoading: boolean;
  error: string | null;
}

const initialState: CommentState = {
  comments: [],
  isLoading: false,
  error: null
}

export const PendingCheckCommentStore = signalStore(
  { providedIn: 'root'},

  withState(initialState),

  withComputed((store) => ({

    visibleComments: computed(() =>
      store.comments().filter(c => c.severity !== 'HINT')
    ),

    hasUnresolvedComments: computed(() =>
      store.comments().some(c => c.status === commentStatus.tbd)
    )

  })),

  withMethods((
    store,
    checkService = inject(CheckService),
    documentService = inject(DocumentService),
    commentService = inject(CommentService)
  ) => ( {

    loadCommentsByCheckId: rxMethod<string | null>(
      pipe(
        tap(() => patchState(store, { isLoading: true, error: null })),
        switchMap((checkId) => {
          if (!checkId) {
            patchState(store, {comments: [], isLoading: false})
            return [];
          }
          return checkService.getCheckCommentsApi(checkId).pipe(
            tapResponse({
              next: (dtos) => {
                const mappedComments = dtos.map(dto => commentService.mapComment(dto, new Map(), new Map()));
                console.warn(`[loadCommentsByCheckId] Mapped comments for checkId=${checkId}:`, mappedComments);
                patchState(store, { comments: mappedComments, isLoading: false })
              },
              error: (err: any) => patchState(store, { error: err.message, isLoading: false })
            })
          )
        })
      )
    ),

    // --- IMPERATIVE MUTATIONEN

    async refetchComments(checkId: string): Promise<void> {
      try {
        const dtos = await firstValueFrom(checkService.getCheckCommentsApi(checkId));
        console.warn(`[refetchComments] Refetched comments for checkId=${checkId}:`, dtos);
        const mappedComments = dtos.map(dto => commentService.mapComment(dto, new Map(), new Map()));
        console.warn(`[refetchComments] Mapped comments for checkId=${checkId}:`, mappedComments);
        patchState(store, { comments: mappedComments, isLoading: false });
      } catch (error: any) {
        patchState(store, { error: error.message, isLoading: false });
        console.error(error);
      }
    },

    async addComment(checkId: string, stageId: number, text: string, severity: string) {
      if (!checkId || !text.trim())
        return;

      patchState(store, { isLoading: true, error:null });
      try {
        await firstValueFrom(checkService.addCommentApi(checkId, stageId, { text: text.trim(), severity}));
        await this.refetchComments(checkId);
      } catch (error: any) {
        patchState(store, { error: error.message, isLoading: false });
        console.error(error);
      }
    },

    async addDocumentComment(checkId: string, stageId: number, documentId: string, text: string, severity: string) {
      if (!checkId || !documentId || !text.trim())
        return;

      patchState(store, {isLoading: true, error: null});
      try {

        const commentDto = await firstValueFrom(
          checkService.addCommentApi(checkId, stageId, {text: text.trim(), severity})
        );

        console.warn(`[addDocumentComment] Adding document comment: checkId=${checkId}, stageId=${stageId}, documentId=${documentId}, text=${text}, severity=${severity}`);

        const linkDocumentResponse = await firstValueFrom(
          documentService.linkCommentToDocumentApi(documentId, commentDto.guid, { visibility: 0 })
        );

        console.warn(`[addDocumentComment] Linking document comment: checkId=${checkId}, stageId=${stageId}, documentId=${documentId}, commentGuid=${commentDto.guid}`);
        console.warn(`[addDocumentComment] Linking document comment: checkId=${checkId}, stageId=${stageId}, documentId=${documentId}, commentGuid=${commentDto.guid}, response=${linkDocumentResponse}`, linkDocumentResponse);

        await this.refetchComments(checkId);

      } catch (error: any) {
        patchState(store, {error: error.message, isLoading: false});
        console.error(error);
      }
    },

    async updateCommentStatus(checkId: string, guid: string, isDone: boolean) {
      if (!checkId) return;
      patchState(store, { isLoading: true });
      try {
        await firstValueFrom(commentService.updateCommentApi(guid, { isDone }));
        await this.refetchComments(checkId);
      } catch (error: any) {
        patchState(store, { error: 'Kommentar-Status aktualisieren fehlgeschlagen', isLoading: false });
        console.error(error);
      }
    },

    async updateCommentSeverity(checkId: string, guid: string, severity: string) {
      if (!checkId) return;
      patchState(store, { isLoading: true });
      try {
        await firstValueFrom(commentService.updateCommentApi(guid, { severity }));
        await this.refetchComments(checkId);
      } catch (error: any) {
        patchState(store, { error: 'Kommentar-Severity aktualisieren fehlgeschlagen', isLoading: false });
        console.error(error);
      }
    },

    async deleteComment(checkId: string, guid: string) {
      patchState(store, { isLoading: true, error: null });

      try {
        await firstValueFrom(commentService.deleteCommentApi(guid));
        await this.refetchComments(checkId);
      } catch(error: any) {
        patchState(store, { error: error.message, isLoading: false });
        console.error(error);
      }
    }

  }))
)
